import React, { useEffect, useState } from 'react'
import {
  BarVisualizer,
  RoomContext,
  useParticipants,
} from '@livekit/components-react'
import { Track } from 'livekit-client'
import livekitService from '../../services/livekitService'

// Wrapper component that provides Room context
const VoiceIntensityWithRoom = ({
  isActive,
  barCount = 5,
  minHeight = 8,
  className = '',
  buttonSize = 200,
  type = 'user',
}) => {
  const [room, setRoom] = useState(null)

  useEffect(() => {
    if (isActive) {
      // Get the room from livekitService
      const currentRoom = livekitService.getRoom()
      if (currentRoom) {
        setRoom(currentRoom)
      }
    } else {
      setRoom(null)
    }
  }, [isActive])

  if (!room) {
    return null
  }

  return (
    <RoomContext.Provider value={room}>
      <VoiceIntensityBars
        isActive={isActive}
        barCount={barCount}
        minHeight={minHeight}
        className={className}
        buttonSize={buttonSize}
        type={type}
      />
    </RoomContext.Provider>
  )
}

// Main voice intensity component that uses LiveKit hooks
const VoiceIntensityBars = ({
  isActive,
  barCount = 6,
  minHeight = 8,
  className = '',
  buttonSize = 200,
  type = 'user',
}) => {
  const participants = useParticipants()

  // Always show bars, but with different states based on isActive

  // Try to find AI participant or use local participant
  let trackRef = null

  // Use LiveKit's native audio level detection to find the speaking participant
  let speakingParticipant = null
  let highestAudioLevel = 0

  // Find the participant with the highest audio level (most likely speaking)
  participants.forEach((participant) => {
    const audioLevel = participant.audioLevel || 0
    if (audioLevel > highestAudioLevel) {
      highestAudioLevel = audioLevel
      speakingParticipant = participant
    }
  })

  // If no participant has audio level, fallback to checking speaking status
  if (!speakingParticipant || highestAudioLevel === 0) {
    speakingParticipant =
      participants.find((p) => p.isSpeaking) || (participants.length > 0 ? participants[0] : null)
  }

  if (speakingParticipant) {
    const audioTrack = speakingParticipant.getTrackPublication(Track.Source.Microphone)
    if (audioTrack && audioTrack.track) {
      trackRef = {
        source: Track.Source.Microphone,
        participant: speakingParticipant,
        publication: audioTrack,
      }
    }
  }

  // If no trackRef and not active, show static bars
  if (!trackRef && !isActive) {
    return (
      <div
        className={`voice-intensity-visualizer ${className}`}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: buttonSize - 18,
          height: buttonSize - 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
          }}
        >
          {Array.from({ length: barCount }, (_, index) => (
            <div
              key={index}
              className="cuekit-voice-intensity-bar"
              style={{
                backgroundColor: '#000000',
                borderRadius: '6px',
                minWidth: '10px',
                minHeight: '12px',
                width: '10px',
                height: '12px',
                opacity: 0.4,
              }}
            />
          ))}
        </div>
      </div>
    )
  }

  if (!trackRef) {
    return null
  }

  return (
    <div
      className={`voice-intensity-visualizer ${className}`}
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: buttonSize - 18,
        height: buttonSize - 18,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <BarVisualizer
        barCount={barCount}
        state="speaking"
        options={{ minHeight, maxHeight: minHeight * 2.5 }}
        trackRef={trackRef}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
        }}
      >
        <span
          className="cuekit-voice-intensity-bar"
          style={{
            backgroundColor: '#000000',
            borderRadius: '4px',
            minWidth: '6px',
            minHeight: '8px',
          }}
        />
      </BarVisualizer>
    </div>
  )
}

// Export the wrapper component
const VoiceIntensityVisualizer = VoiceIntensityWithRoom

export default VoiceIntensityVisualizer
